import { createContext, useContext } from 'react';
import type { EvalPoint } from 'components/well-3d-viewer/eval-point-md-slider';

// Slider-driven state lives in a context so ControlsPanel (and its wellbore /
// structure-grid panels) can be memoised: the slider changes on every drag, and
// without this split each change would re-render the whole left-panel tree.
export interface SliderContextValue {
  selectedEvalPointMd: number | null;
  onEvalPointMdChange: (md: number | null) => void;
  evalPoints: EvalPoint[];
  activeEvalPointCoords: { latitude: number; longitude: number } | undefined;
  subjectWellAzimuth: number | undefined;
  sliceHalfWidthFt: number | undefined;
  sliceFlipped: boolean;
  highlightedSurvey: GeoJSON.Feature | null;
  /** Status of `highlightedSurvey`; drives the inset map's highlight color. */
  highlightedSurveyStatus: string | null;
  /** UWI for offset-well highlight; routed to the inset map's tile-filter path. */
  highlightedUwi: string | null;
  /**
   * True when the spacing subject is a VERTICAL well. A vertical has no lateral
   * to place a reference point along, so the controls panel swaps the Reference
   * Point (MD) slider for the Gun Barrel Azimuth slider.
   */
  subjectIsVertical: boolean;
  /**
   * Committed Gun Barrel Azimuth (deg), or null when unchosen. Null is NOT 0 —
   * see buildSpacingParameters: an absent azimuth is how the BE knows not to
   * space a vertical yet, while 0 is due North.
   */
  gunBarrelAzimuthDeg: number | null;
  /** Commits a new azimuth. Marks spacing stale; spacing is re-run on demand. */
  onGunBarrelAzimuthChange: (deg: number) => void;
}

const SliderContext = createContext<SliderContextValue | null>(null);

export const SliderContextProvider = SliderContext.Provider;

/**
 * Returns the slider context, or null when used outside a provider.
 * Connector components consult the result and skip rendering slider-only
 * UI (e.g. the slice rectangle, MD slider) when null — this lets ControlsPanel
 * also be rendered in non-slider contexts (the well-3d-viewer modal) without
 * errors.
 * @return {SliderContextValue | null} The current slider context value, or
 *   null when no SliderContextProvider is mounted above the caller.
 */
export function useSliderContext(): SliderContextValue | null {
  return useContext(SliderContext);
}
